<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useDestinationStore } from '../../../store/destinationStore'

const SERVER_URL = process.env.VUE_APP_URL;
import {mapGetters} from "vuex";
import lookup from "country-code-lookup";

const emit = defineEmits(['selectCountry']);

const { t } = useI18n({ useScope: 'global' })
const store = useStore()
const destinationStore = useDestinationStore();

const loading = ref(false)

const lang = computed(() => store.getters['getSelectedLanguage'])
const orderData = computed(() => store.getters['purchase/getOrderData'])
const activeOrderDataIndex = computed(() => store.getters['purchase/getActiveOrderDataIndex'])
const isSelectedCountryNotPopular = computed(() => {
    const activeCountryId = destinationStore.getSelectedDestinations[0]?.id;

    return activeCountryId && !destinationStore.getPopularDestinations.find(country => country.id === activeCountryId);
})
const notInPopularCountryImgUrl = computed(() => {
    const countryName = destinationStore.getSelectedDestinations[0]?.name;

    if (countryName) {
        const countryData = lookup.byCountry(countryName);

        if (countryData && countryData.iso2) {
            return `https://flagsapi.com/${countryData.iso2}/flat/64.png`;
        }
    }

    return null;
})

async function loadPopularCountries() {
    loading.value = true
    await destinationStore.fetchPopularDestination({
        limit: 10
    })
    loading.value = false
}

function selectCountry(country) {
    destinationStore.setFirstSelectedDestination({"destination": country});
    // emit('selectCountry', country)
}

onMounted(() => {
    loadPopularCountries()
})
</script>

<template>
    <div class="purchase-rent-country">
        <div class="purchase-rent-country__title">
            <h1>{{ t("purchase-rent-page.popular_countries.title") }}</h1>
        </div>

        <div v-if="!loading" class="purchase-rent-country__items">
            <div
                v-if="isSelectedCountryNotPopular"
                class="purchase-rent-country__item active"
            >
                <img
                    class="purchase-rent-country__item-flag-img"
                    :src="notInPopularCountryImgUrl"
                    :alt="destinationStore.getSelectedDestinations[0]?.name"
                >

                <div class="purchase-rent-country__item-info">
                    <p class="purchase-rent-country__item-info-name">
                        {{ destinationStore.getSelectedDestinations[0]?.name }}
                    </p>
                </div>
            </div>

            <div v-for="(country, i) in destinationStore.getPopularDestinations" :key="country.id" class="purchase-rent-country__item"
                :class="{ active: country.id === destinationStore.getSelectedDestinations[0]?.id }"
                @click="selectCountry(country)">
                <img v-if="i < 3" class="purchase-rent-country__item-top-img"
                    :src="`${SERVER_URL}/image/top-${i + 1}.png`" alt="top-image" />

                <img class="purchase-rent-country__item-flag-img"
                    :src="`${SERVER_URL}/image/flags/${country.img}`"
                    :alt="country.name" />

                <p class="purchase-rent-country__item-info-name">
                    {{ lang === 'jp' && country.name_jp ? country.name_jp : country.name }}
                </p>
            </div>
        </div>

        <!-- Loading Skeleton -->
        <div v-if="loading" class="flex flex-wrap justify-center gap-4">
            <div role="status" class="flex items-center justify-center h-40 w-65 bg-sky-100 rounded-lg animate-pulse"></div>
            <div role="status" class="flex items-center justify-center h-40 w-65 bg-sky-100 rounded-lg animate-pulse"></div>
            <div role="status" class="flex items-center justify-center h-40 w-65 bg-sky-100 rounded-lg animate-pulse"></div>
            <div role="status" class="flex items-center justify-center h-40 w-65 bg-sky-100 rounded-lg animate-pulse"></div>
            <div role="status" class="flex items-center justify-center h-40 w-65 bg-sky-100 rounded-lg animate-pulse"></div>
            <div role="status" class="flex items-center justify-center h-40 w-65 bg-sky-100 rounded-lg animate-pulse"></div>
            <div role="status" class="flex items-center justify-center h-40 w-65 bg-sky-100 rounded-lg animate-pulse"></div>
            <div role="status" class="flex items-center justify-center h-40 w-65 bg-sky-100 rounded-lg animate-pulse"></div>
            <div role="status" class="flex items-center justify-center h-40 w-65 bg-sky-100 rounded-lg animate-pulse"></div>
            <div role="status" class="flex items-center justify-center h-40 w-65 bg-sky-100 rounded-lg animate-pulse"></div>
            <span class="sr-only">Loading...</span>
        </div>
    </div>
</template>

<style lang="scss">
.purchase-rent-country {
    position: relative;
    margin-top: 2rem;

    &__title {
        margin-bottom: 2rem;

        h1 {
            text-align: center;
            font-size: 24px;
            font-weight: 400;
            margin-bottom: .5rem;
        }
    }

    &__items {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
    }

    &__item {
        position: relative;
        background: #fff;
        width: 259px;
        height: 160px;
        border-radius: 8px;
        border: 2px solid #7DD3FC;
        cursor: pointer;
        margin: .5rem;
        padding: 0.5rem 1rem;

        &-flag-img {
            width: 75px;
            margin-bottom: .5rem;
            margin-left: 74px;
            margin-top: 10px;
            position: relative;
            display: inline-block;
        }

        &-info-name {
            font-size: 20px;
            font-weight: 400;
            margin-bottom: .5rem;
            text-align: center;
            text-transform: uppercase;
        }

        &-info-plan {
            font-size: 16px;
            font-weight: 500;
            text-transform: uppercase;
        }

        &-info-price {
            font-size: 16px;
            font-weight: 400;
        }

        &-top-img {
            position: absolute;
            right: 1rem;
            top: -0.5rem;
            width: 50px;
            height: 50px;
        }

        &:hover,
        &.active {
            background: #7DD3FC;
            color: #fff;
        }
    }
}

@media (max-width: 567px) {
    .purchase-rent-country {
        &__item {
            width: 100%;
            height: 80px;
            display: flex;
            align-items: center;

            &-flag-img {
                margin-right: 1rem;
                margin-bottom: 0;
                margin-left: 0;
                margin-top: 0;
            }

            &-info {
                width: 100%;

                &-name {
                    text-align: left;
                }
            }
        }
    }
}
</style>